import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  MenuItem,
  Chip,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';

const OrganizerQuestionnaire = ({ 
  clientName, 
  clientId,
  clients = [],
  organizerTypes, 
  onClose, 
  onSubmit,
  onSendToClient,
  saving = false,
  error = null,
  onClearError
}) => {
  const [currentSection, setCurrentSection] = useState('personal');
  const [formData, setFormData] = useState({
    name: '',
    tax_year: new Date().getFullYear(),
    client_type: 'existing_client',
    organizer_type: '',
    client: clientId || '',
    due_date: '',
    notes: '',
    assigned_to: [],
    responses: {}
  });
  const isClientSelectMode = !clientId && clients?.length > 0;
  const selectedClient = formData.client && clients?.find(c => c.id == formData.client);
  const displayClientName = clientName || (selectedClient?.display_name || selectedClient?.primary_contact?.display_name || (formData.client ? `Client ${formData.client}` : ''));
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = React.useRef(null);

  // Questionnaire sections
  const sections = [
    { id: 'personal', title: 'Personal', icon: '👤' },
    { id: 'questionnaire', title: 'Questionnaire', icon: '📋' },
    { id: 'income', title: 'Income', icon: '💰' },
    { id: 'expenses', title: 'Expenses', icon: '💳' },
    { id: 'additional', title: 'Additional Information', icon: 'ℹ️' },
    { id: 'estimated_taxes', title: 'Estimated Taxes', icon: '📊' },
    { id: 'upload', title: 'Upload Documents', icon: '📁' }
  ];

  // Sample questions for each section
  const questions = {
    personal: [
      {
        id: 'first_name',
        type: 'text',
        label: 'First Name',
        required: true,
        placeholder: 'Enter first name'
      },
      {
        id: 'last_name',
        type: 'text',
        label: 'Last Name',
        required: true,
        placeholder: 'Enter last name'
      },
      {
        id: 'ssn',
        type: 'text',
        label: 'Social Security Number',
        required: true,
        placeholder: 'XXX-XX-XXXX'
      },
      {
        id: 'date_of_birth',
        type: 'date',
        label: 'Date of Birth',
        required: true
      },
      {
        id: 'filing_status',
        type: 'dropdown',
        label: 'Filing Status',
        required: true,
        options: [
          'Single',
          'Married Filing Jointly',
          'Married Filing Separately',
          'Head of Household',
          'Qualifying Widow(er)'
        ]
      }
    ],
    income: [
      {
        id: 'income_sources',
        type: 'checkbox_group',
        label: 'Type(s) of income received during 2023',
        description: 'Select all that apply, even if you didn\'t receive a form.',
        options: [
          { id: 'employment_w2', label: 'Employment (W-2)' },
          { id: 'retirement_1099r', label: 'Retirement distribution (1099-R)' },
          { id: 'social_security', label: 'Social Security (SSA-1099)' },
          { id: 'self_employment', label: 'Self-employment (1099-NEC or 1099-K)' },
          { id: 'state_tax_refund', label: 'State or local tax refund (1099-G)' },
          { id: 'unemployment', label: 'Unemployment (1099-G)' },
          { id: 'interest', label: 'Interest (1099-INT)' },
          { id: 'dividends', label: 'Dividends (1099-DIV)' },
          { id: 'stock_sales', label: 'Sold stock or investments (1099-B)' },
          { id: 'canceled_debt', label: 'Canceled debt (1099-C)' },
          { id: 'rents', label: 'Rents (1099-MISC)' },
          { id: 'royalties', label: 'Royalties (1099-MISC)' },
          { id: 'esa_529', label: 'ESA or 529 distribution (1099-Q)' },
          { id: 'hsa_msa', label: 'HSA or MSA distribution (1099-SA)' },
          { id: 'partnership', label: 'Partnership (Schedule K-1)' },
          { id: 's_corp', label: 'S-corporation (Schedule K-1)' },
          { id: 'estate_trust', label: 'Estate or trust (Schedule K-1)' },
          { id: 'gambling', label: 'Gambling (W-2G)' },
          { id: 'farming', label: 'Farming' },
          { id: 'other_income', label: 'Other income' }
        ]
      },
      {
        id: 'income_comments',
        type: 'textarea',
        label: 'Comments',
        placeholder: 'Enter any additional information or questions here'
      }
    ],
    expenses: [
      {
        id: 'mortgage_interest',
        type: 'number',
        label: 'Mortgage Interest',
        placeholder: 'Enter amount'
      },
      {
        id: 'property_taxes',
        type: 'number',
        label: 'Property Taxes',
        placeholder: 'Enter amount'
      },
      {
        id: 'charitable_contributions',
        type: 'number',
        label: 'Charitable Contributions',
        placeholder: 'Enter amount'
      },
      {
        id: 'medical_expenses',
        type: 'number',
        label: 'Medical and Dental Expenses',
        placeholder: 'Enter amount'
      }
    ],
    additional: [
      {
        id: 'estimated_tax_payments',
        type: 'boolean',
        label: 'Did you make any estimated tax payments?'
      },
      {
        id: 'estimated_tax_amount',
        type: 'number',
        label: 'Total Estimated Tax Payments',
        placeholder: 'Enter amount'
      },
      {
        id: 'additional_comments',
        type: 'textarea',
        label: 'Additional Comments',
        placeholder: 'Any additional information or questions'
      }
    ],
    estimated_taxes: [
      {
        id: 'quarterly_payments',
        type: 'number',
        label: 'Q1 Estimated Tax Payment',
        placeholder: 'Enter amount'
      },
      {
        id: 'q2_payments',
        type: 'number',
        label: 'Q2 Estimated Tax Payment',
        placeholder: 'Enter amount'
      },
      {
        id: 'q3_payments',
        type: 'number',
        label: 'Q3 Estimated Tax Payment',
        placeholder: 'Enter amount'
      },
      {
        id: 'q4_payments',
        type: 'number',
        label: 'Q4 Estimated Tax Payment',
        placeholder: 'Enter amount'
      }
    ]
  };

  // Fetch all users and teams when component mounts
  useEffect(() => {
    fetchAllUsers();
    fetchAllTeams();
  }, []);

  const fetchAllTeams = async () => {
    try {
      const response = await enhancedApi.getTeams();
      const data = response.data;
      setAllTeams(Array.isArray(data) ? data : (data?.data || []));
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  // Auto-select first organizer type when types are loaded and none selected
  useEffect(() => {
    if (organizerTypes?.length > 0 && !formData.organizer_type) {
      setFormData(prev => ({ ...prev, organizer_type: organizerTypes[0].id }));
    }
  }, [organizerTypes, formData.organizer_type]);

  // Auto-save functionality
  useEffect(() => {
    const autoSave = setTimeout(() => {
      if (Object.keys(formData.responses).length > 0) {
        setAutoSaveStatus('Saving...');
        // Simulate auto-save
        setTimeout(() => {
          setAutoSaveStatus('All changes saved');
          setTimeout(() => setAutoSaveStatus(''), 2000);
        }, 500);
      }
    }, 1000);

    return () => clearTimeout(autoSave);
  }, [formData.responses]);

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await api.get('/api/multi-tenant/users/');
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInputChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      responses: {
        ...prev.responses,
        [questionId]: value
      }
    }));
  };

  const handleCheckboxChange = (questionId, optionId, checked) => {
    setFormData(prev => {
      const currentValues = prev.responses[questionId] || [];
      const newValues = checked
        ? [...currentValues, optionId]
        : currentValues.filter(id => id !== optionId);
      
      return {
        ...prev,
        responses: {
          ...prev.responses,
          [questionId]: newValues
        }
      };
    });
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  const handleTeamSelect = (team) => {
    if (!team?.users) return;
    const usernames = Array.isArray(team.users) ? team.users : [];
    const userIds = usernames
      .map(uname => allUsers.find(u => u.username === uname)?.id)
      .filter(Boolean);
    setFormData(prev => ({
      ...prev,
      assigned_to: [...new Set([...prev.assigned_to, ...userIds])]
    }));
  };

  const filteredUsers = allUsers.filter(user =>
    (user.username || user.first_name || user.last_name || 'Unknown User')
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const getSelectedUserNames = () => {
    return formData.assigned_to.map(userId => {
      const user = allUsers.find(u => u.id === userId);
      return user ? (user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User') : 'Unknown User';
    });
  };

  const renderQuestion = (question) => {
    const value = formData.responses[question.id] ?? '';

    switch (question.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            required={question.required}
          />
        );
      case 'date':
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            required={question.required}
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );
      case 'dropdown':
        return (
          <TextField
            select
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            required={question.required}
          >
            <MenuItem value="">Select an option</MenuItem>
            {(question.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={4}
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
          />
        );
      case 'boolean':
        return (
          <RadioGroup
            row
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={(e) => handleInputChange(question.id, e.target.value === 'yes')}
          >
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
          </RadioGroup>
        );
      case 'checkbox_group':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {question.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{question.description}</Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              {(question.options || []).map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={(value || []).includes(option.id)}
                      onChange={(e) => handleCheckboxChange(question.id, option.id, e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">{option.label}</Typography>}
                />
              ))}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const getCurrentSectionTitle = () => {
    const section = sections.find(s => s.id === currentSection);
    return section ? section.title : 'Personal';
  };

  const getCurrentSectionSubtitle = () => {
    if (currentSection === 'income') return 'Income sources';
    return '';
  };

  const handleNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === currentSection);
    if (currentIndex < sections.length - 1) {
      setCurrentSection(sections[currentIndex + 1].id);
    }
  };

  const handlePreviousSection = () => {
    const currentIndex = sections.findIndex(s => s.id === currentSection);
    if (currentIndex > 0) {
      setCurrentSection(sections[currentIndex - 1].id);
    }
  };

  const handleSendToClient = () => {
    onSendToClient(formData);
  };

  const handleSaveAndClose = () => {
    if (saving) return;
    onSubmit({ ...formData, uploadedFiles });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'grey.50', zIndex: 1300, display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, py: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {displayClientName || 'New Organizer'} &gt; {(displayClientName ? `${displayClientName} ` : '')}{formData.tax_year} Organizer
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCurrentSectionTitle()}
              {getCurrentSectionSubtitle() && (
                <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>{getCurrentSectionSubtitle()}</Typography>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {error && <Typography variant="body2" color="error">{error}</Typography>}
            {autoSaveStatus && !error && <Typography variant="body2" color="success.main">{autoSaveStatus}</Typography>}
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Paper elevation={0} sx={{ width: 280, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', p: 2, borderRadius: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>1040 Organizer</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button variant="contained" size="small" onClick={handleSendToClient} sx={{ textTransform: 'none' }}>Send</Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {sections.map((section) => (
              <Button
                key={section.id}
                fullWidth
                onClick={() => setCurrentSection(section.id)}
                startIcon={<span>{section.icon}</span>}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontWeight: currentSection === section.id ? 600 : 400,
                  bgcolor: currentSection === section.id ? 'primary.50' : 'transparent',
                  color: currentSection === section.id ? 'primary.main' : 'text.primary',
                  borderLeft: '4px solid',
                  borderLeftColor: currentSection === section.id ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: currentSection === section.id ? 'primary.50' : 'grey.100' }
                }}
              >
                {section.title}
              </Button>
            ))}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>Upload Documents</Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setCurrentSection('upload')}
              sx={{
                borderStyle: 'dashed',
                textTransform: 'none',
                borderColor: currentSection === 'upload' ? 'primary.main' : 'divider',
                bgcolor: currentSection === 'upload' ? 'primary.50' : 'transparent'
              }}
            >
              Upload Documents
              {uploadedFiles.length > 0 && (
                <Chip label={uploadedFiles.length} size="small" sx={{ ml: 1 }} />
              )}
            </Button>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Box sx={{ maxWidth: 640 }}>
            {currentSection === 'personal' && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Organizer Details</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <TextField select label="Tax year *" size="small" fullWidth value={formData.tax_year} onChange={(e) => setFormData(prev => ({ ...prev, tax_year: e.target.value }))}>
                    <MenuItem value={new Date().getFullYear()}>{new Date().getFullYear()}</MenuItem>
                    <MenuItem value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</MenuItem>
                  </TextField>
                  <TextField select label="Client type *" size="small" fullWidth value={formData.client_type} onChange={(e) => setFormData(prev => ({ ...prev, client_type: e.target.value }))}>
                    <MenuItem value="existing_client">Existing client</MenuItem>
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="business">Business</MenuItem>
                  </TextField>
                  {isClientSelectMode ? (
                    <TextField select label="Client *" size="small" fullWidth value={formData.client} onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))} required>
                      <MenuItem value="">Select client</MenuItem>
                      {clients.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.display_name || c.primary_contact?.display_name || `Client ${c.id}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField label="Client" size="small" fullWidth value={clientName || ''} disabled />
                  )}
                  <TextField select label="Organizer type *" size="small" fullWidth value={formData.organizer_type} onChange={(e) => setFormData(prev => ({ ...prev, organizer_type: e.target.value }))}>
                    <MenuItem value="">Select organizer type</MenuItem>
                    {(organizerTypes || []).map((type) => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Organizer name *" size="small" fullWidth value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder={`${displayClientName ? `${displayClientName} ` : ''}${formData.tax_year} Organizer`} />
                  <TextField label="Due date" size="small" fullWidth type="date" value={formData.due_date} onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))} />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Assignee(s)</Typography>
                  {allTeams.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Quick assign by team</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {allTeams.map((team) => (
                          <Button key={team.id} variant="outlined" size="small" onClick={() => handleTeamSelect(team)} sx={{ textTransform: 'none' }}>
                            {team.icon_color && <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: team.icon_color, mr: 0.5, display: 'inline-block' }} />}
                            {team.name}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ position: 'relative' }}>
                    <TextField size="small" fullWidth placeholder="Search for assignee" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onFocus={() => setShowUserDropdown(true)} onBlur={() => setTimeout(() => setShowUserDropdown(false), 150)} />
                    {showUserDropdown && (
                    <Paper sx={{ mt: 1, maxHeight: 200, overflow: 'auto', position: 'absolute', zIndex: 1200, minWidth: '100%' }}>
                      {loadingUsers ? <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Loading users...</Typography></Box> : filteredUsers.length > 0 ? filteredUsers.map((user) => (
                        <FormControlLabel key={user.id} control={<Checkbox size="small" checked={formData.assigned_to.includes(user.id)} onChange={() => handleUserToggle(user.id)} />} label={<Typography variant="body2">{user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User'}</Typography>} sx={{ display: 'block', mx: 1 }} />
                      )) : <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No users found</Typography></Box>}
                    </Paper>
                    )}
                  </Box>
                  {formData.assigned_to.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Selected assignees:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getSelectedUserNames().map((name, idx) => (
                          <Chip key={idx} label={name} size="small" onDelete={() => handleUserToggle(formData.assigned_to[idx])} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {currentSection === 'upload' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Upload Documents</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add tax documents (W-2s, 1099s, receipts, etc.) to include with this organizer.
                  </Typography>
                  <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: 'none' }} />
                  <Button fullWidth variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} sx={{ borderStyle: 'dashed', py: 3, textTransform: 'none' }}>
                    Choose Files to Upload
                  </Button>
                </Box>
                {uploadedFiles.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Selected files ({uploadedFiles.length})</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {uploadedFiles.map((file, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>{file.name}</Typography>
                          <Button size="small" color="error" onClick={() => handleRemoveFile(i)}>Remove</Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {questions[currentSection]?.map((question) => (
                  <Box key={question.id}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {question.label}
                      {question.required && <Typography component="span" color="error"> *</Typography>}
                    </Typography>
                    {renderQuestion(question)}
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', gap: 2, flexWrap: 'wrap' }}>
              <Button onClick={handlePreviousSection} disabled={sections.findIndex((s) => s.id === currentSection) === 0} startIcon={<PrevIcon />} sx={{ textTransform: 'none', minWidth: 140 }}>
                Previous section
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={handleSaveAndClose} disabled={saving} sx={{ textTransform: 'none', minWidth: 120 }}>
                  {saving ? 'Saving...' : 'Save & Close'}
                </Button>
                {sections.findIndex((s) => s.id === currentSection) === sections.length - 1 ? (
                  <Button variant="contained" onClick={handleSaveAndClose} disabled={saving} sx={{ textTransform: 'none', minWidth: 140 }}>
                    {saving ? 'Saving...' : 'Save & Finish'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleNextSection} disabled={saving} endIcon={<NextIcon />} sx={{ textTransform: 'none', minWidth: 140 }}>
                    Next section
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizerQuestionnaire;



